import { Injectable } from '@angular/core';
import {
  ConfirmationService,
  ConfirmEventType,
  MessageService,
} from 'primeng/api';
import { Indicator } from '../models/indicator';
import { Category } from '../models/category';
import { DataLayer } from '../models/data-layer';
import { DataLayerType } from '../models/data-layer-type';
import { BookmarkCategory } from '../models/bookmark-category';
import { Message } from 'primeng/api/message';
import { Bookmark } from '../models/bookmark';

@Injectable({
  providedIn: 'root',
})
export class ConfirmationDialogService {
  constructor(
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {}

  entityTypeBlackList: string[] = ['History'];

  // Store publishDataLayerId value from dialog
  private _publishDataLayerId = false;

  /**
   * Displays a message using a message service.
   *
   * @param {string} summary - A brief summary or title of the message.
   * @param {string} message - The detailed content of the message.
   * @param {number} life - Message life-time
   * @return {void} Does not return a value.
   */
  showMessage(summary: string, message?: string, life = 3000): void {
    this.messageService.add({
      severity: 'info',
      summary: summary,
      detail: message,
      life: life,
    });
  }

  /**
   * Adds an error message to the message service with predefined severity and summary.
   *
   * @param {string} detail - The detailed error message to be displayed.
   * @param {string} summary - The summary error message to be displayed.
   **/
  addErrorMessage(
    detail: string,
    summary = 'Etwas ist schiefgelaufen! Der Vorgang konnte nicht abgeschlossen werden.'
  ): void {
    this.messageService.add({
      severity: 'error',
      summary,
      detail,
    });
  }

  /**
   * Adds a success message to the message service, indicating that an item has been added to the Atlas.
   *
   * @param {string} itemName - The name of the item that was added.
   * @return {void} Does not return*/
  addMessage(item: any, entityType: string): void {
    if (this.entityTypeBlackList.includes(entityType)) return;

    const itemName = this.getTitleByEntity(item, entityType);
    this.messageService.add({
      severity: 'success',
      summary: `"${itemName}" wurde Ihrem Atlas hinzugefügt!`,
    });
  }

  /**
   * Updates the message with a success notification indicating that the specified item has been updated.
   *
   * @param {string} itemName - The name of the item that has been updated.
   * @return {void} This method does not return a value.
   */
  updateMessage(item: any, entityType: string): void {
    if (this.entityTypeBlackList.includes(entityType)) return;
    const itemName = this.getTitleByEntity(item, entityType);
    this.messageService.add({
      severity: 'success',
      summary: `"${itemName}" wurde aktualisiert!`,
    });
  }

  /**
   * Removes a message related to a specific item and entity type. It checks if the entity type is in the blacklist
   * and, if not, displays a success message indicating that the item has been deleted.
   *
   * @param {any} item - The item to be removed.
   * @param {string} entityType - The type of the entity associated with the item.
   * @return {void} This method does not return a value.
   */
  removeMessage(item: any, entityType: string): void {
    if (this.entityTypeBlackList.includes(entityType)) return;
    const itemName = this.getTitleByEntity(item, entityType);
    this.messageService.add({
      severity: 'success',
      summary: 'Löschen erfolgreich!',
      detail: `"${itemName}" wurde gelöscht!`,
    });
  }

  /**
   * Adds a success message to notify that multiple items of the specified type have been added.
   *
   * @param {string} entityType - The name of the type of items that have been added. This name is included in the success message.
   * @return {void} This method does not return a value.
   */
  addMultipleMessage(entityType: string): void {
    if (this.entityTypeBlackList.includes(entityType)) return;
    const title = this.getTitleByEntityMultiple(entityType);
    this.messageService.add({
      severity: 'success',
      summary: `${title} wurden hinzugefügt!`,
    });
  }

  /**
   * Updates multiple messages and displays a success notification indicating the type that was updated.
   *
   * @param {string} entityType - The name of the type that has been updated.
   * @return {void} This method does not return a value.
   */
  updateMultipleMessage(entityType: string): void {
    if (this.entityTypeBlackList.includes(entityType)) return;
    const title = this.getTitleByEntityMultiple(entityType);
    this.messageService.add({
      severity: 'success',
      summary: `${title} wurden aktualisiert!`,
    });
  }

  /**
   * Removes multiple messages and displays a success notification indicating that the specified type has been deleted.
   *
   * @param {string} entityType - The name of the type of messages to be removed.
   * @return {void} This method does not return any value.
   */
  removeMultipleMessage(entityType: string): void {
    if (this.entityTypeBlackList.includes(entityType)) return;
    const title = this.getTitleByEntityMultiple(entityType);
    this.messageService.add({
      severity: 'success',
      summary: `${title} wurden gelöscht!`,
    });
  }

  /**
   * Adds a custom message to the message service.
   *
   * @param {Message} message - The message to be added to the message service.
   * @return {void} This method does not return a value.
   */
  customMessage(message: Message): void {
    this.messageService.add(message);
  }

  /**
   * Opens a confirmation dialog to confirm the deletion of an entity.
   *
   * @param {Event | undefined} event - The event triggering the confirmation dialog, which defines the context target.
   * @param {string} itemName - The name of the item to be deleted.
   * @param {string} entityType - The type of the entity being deleted (e.g., file, user, etc.).
   * @param {Function} onAccept - A callback function executed upon confirming the deletion.
   * @param {string} overwriteMessage - A callback function executed upon confirming the deletion.
   * @return {void} Does not return a value.
   */
  confirmDeletion(
    event: Event | undefined,
    itemName: string,
    entityType: string,
    onAccept: () => void,
    overwriteMessage?: string
  ): void {
    this.confirmationService.confirm({
      target: event?.target as EventTarget,
      message:
        overwriteMessage ||
        `Möchten Sie <i>"${itemName}"</i> wirklich löschen?`,
      header: `${entityType} löschen`,
      icon: 'fas fa-circle-exclamation',
      acceptIcon: 'p-button-icon p-button-icon-left fas fa-trash',
      rejectIcon: 'p-button-icon p-button-icon-left fas fa-times',
      acceptLabel: 'ja, löschen',
      rejectLabel: 'nein, abbrechen',
      acceptButtonStyleClass: 'p-button-danger p-button-rounded',
      rejectButtonStyleClass: 'p-button-text p-button-rounded',
      accept: () => {
        onAccept();
        //this.messageService.add({
        //  severity: 'info',
        //  summary: `${entityType}`,
        //  detail: `${itemName} wurde gelöscht`,
        //});
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Abgebrochen',
          detail: `${entityType} "${itemName}" wurde nicht gelöscht`,
        });
      },
    });
  }

  /**
   * Confirms the deletion of an indicator by showing a confirmation dialog.
   * Executes the provided callback function if the deletion is confirmed.
   *
   * @param {Event | undefined} event - The event associated with the deletion action, if any.
   * @param {Indicator} indicator - The indicator object to be deleted.
   * @param {Function} onAccept - The callback function to execute when the deletion is confirmed.
   * @param {string} [key='indicatorDeleteDialog'] - The unique key for the confirmation dialog, default is 'indicatorDeleteDialog'.
   * @return {void} This method does not return a value.
   */
  confirmIndicatorDeletion(
    event: Event | undefined,
    indicator: Indicator,
    onAccept: () => void,
    key = 'indicatorDeleteDialog'
  ): void {
    this.confirmationService.confirm({
      target: event?.target as EventTarget,
      key: key,
      accept: () => {
        onAccept();
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Abgebrochen',
          detail: `${indicator.name} wurde nicht gelöscht`,
        });
      },
    });
  }

  /**
   * Confirms the deletion of a category with a confirmation dialog. If the operation is accepted,
   * a success message is displayed; otherwise, a cancellation message is shown.
   *
   * @param {Event | undefined} event - The event that triggered the confirmation dialog. Can be undefined.
   * @param {Category} category - The category object to be deleted.
   * @param {Function} onAccept - A callback function to execute if the deletion is confirmed.
   * @param {string} [key='categoryDeleteDialog'] - The key used to identify the dialog. Defaults to 'categoryDeleteDialog'.
   * @return {void} This method does not return a value.
   */
  confirmCategoryDeletion(
    event: Event | undefined,
    category: Category,
    onAccept: () => void,
    key = 'categoryDeleteDialog'
  ): void {
    this.confirmationService.confirm({
      target: event?.target as EventTarget,
      key: key,
      accept: () => {
        onAccept();
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Abgebrochen',
          detail: `${category.name} wurde nicht gelöscht`,
        });
      },
    });
  }

  /**
   * Sets the publishDataLayerId value that will be passed to the accept callback.
   * This is called by the confirm dialog component before accepting.
   *
   * @param {boolean} value - The value to store.
   * @return {void}
   */
  setPublishDataLayerId(value: boolean): void {
    this._publishDataLayerId = value;
  }

  /**
   * Retrieves the publish data layer ID status.
   *
   * @return {boolean} The current value of the publish data layer ID.
   */
  getPublishDataLayerId(): boolean {
    return this._publishDataLayerId;
  }

  /**
   * Displays a confirmation dialog for publishing data. Executes the provided action if the user accepts,
   * and displays a corresponding message for both acceptance and rejection scenarios.
   *
   * @param {Function} onAccept - A callback function to execute if the user confirms the action.
   * @param {string} [header] - An optional header string for the confirmation dialog.
   * @param {string} [key='publishDialog'] - An optional key to identify the confirmation dialog instance.
   * @return {void} No return value.
   */
  confirmPublishData(
    onAccept: (publishDataLayerId?: boolean) => void,
    header?: string,
    key = 'publishDialog'
  ): void {
    // Reset the value when creating a new confirmation
    this._publishDataLayerId = false;

    this.confirmationService.confirm({
      header: header,
      key: key,
      accept: () => {
        onAccept(this._publishDataLayerId);
        this.messageService.add({
          severity: 'info',
          summary: 'Bestätigt',
          detail: 'Ihre Daten wurden veröffentlicht',
          life: 3000,
        });
        // Reset after use
        this._publishDataLayerId = false;
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Abgebrochen',
          detail: 'Ihre Daten wurden nicht veröffentlicht',
          life: 3000,
        });
        // Reset after use
        this._publishDataLayerId = false;
      },
    });
  }

  /**
   * Displays a confirmation dialog for locking data. If confirmed, executes the provided callback.
   *
   * @param {function} onAccept - The callback function to execute when the confirmation is accepted.
   * @param {string} [header] - Optional header text to display in the confirmation dialog.
   * @param {string} [key='lockDataDialog'] - Optional identifier for the confirmation dialog. Defaults to 'lockDataDialog'.
   * @return {void} This method does not return anything.
   */
  confirmLockData(
    onAccept: () => void,
    header?: string,
    key = 'lockDataDialog'
  ): void {
    this.confirmationService.confirm({
      header: header,
      key: key,
      accept: () => {
        onAccept();
        this.messageService.add({
          severity: 'info',
          summary: 'Bestätigt',
          detail: 'Ihre Daten wurden gesperrt',
          life: 3000,
        });
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Abgebrochen',
          detail: 'Ihre Daten wurden nicht gesperrt',
          life: 3000,
        });
      },
    });
  }

  /**
   * Displays a confirmation dialog to confirm the deletion of a data layer.
   *
   * @param {Event | undefined} event - The event that triggered the confirmation. May be undefined if no event is associated.
   * @param {DataLayer} dataLayer - The data layer object that is being considered for deletion.
   * @param {Function} onAccept - The callback function to execute if the deletion is confirmed.
   * @return {void} This method does not return a value.
   */
  confirmDataLayerDeletion(
    event: Event | undefined,
    dataLayer: DataLayer,
    onAccept: () => void
  ): void {
    this.confirmationService.confirm({
      key: 'dataLayerDeleteDialog',
      accept: () => {
        onAccept();
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Abgebrochen',
          detail: `${dataLayer.year} wurde nicht gelöscht`,
          life: 3000,
        });
      },
    });
  }

  confirmDataLayerPublication(
    event: Event | undefined,
    dataLayer: DataLayer,
    onAccept: () => void
  ): void {
    this.confirmationService.confirm({
      key: 'dataLayerPublicationDialog',
      accept: () => {
        onAccept();
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Abgebrochen',
          detail: `${dataLayer.year} wurde nicht ${dataLayer.published ? 'intern geschaltet' : 'veröffentlicht'}`,
          life: 3000,
        });
      },
    });
  }

  /**
   * Confirms the deletion of a data layer's parent and executes the appropriate actions based on user acceptance or rejection.
   *
   * @param {Event | undefined} event - The triggering event, if available. Can be undefined.
   * @param {DataLayerType} dataLayerType - The type of the data layer being deleted, providing contextual details for the action.
   * @param {Function} onAccept - A callback function executed if the user confirms the deletion.
   * @return {void} This method does not return a value.
   */
  confirmDataLayerParentDeletion(
    event: Event | undefined,
    dataLayerType: DataLayerType,
    onAccept: () => void
  ): void {
    this.confirmationService.confirm({
      key: 'dataLayerParentDeleteDialog',
      accept: () => {
        onAccept();
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Abgebrochen',
          detail: `${dataLayerType.name} wurde nicht gelöscht`,
          life: 3000,
        });
      },
    });
  }

  /**
   * Confirms the deletion of a bookmark category and executes corresponding actions based on user response.
   * Displays a confirmation dialog allowing the user to accept or reject the deletion action.
   *
   * @param {Event | undefined} event - The event that triggered the confirmation dialog. Can be undefined if not explicitly triggered by a user action.
   * @param {BookmarkCategory} bookmarkCategory - The bookmark category to be deleted, containing details such as title.
   * @param {Function} onAccept - Callback function to execute when the deletion is confirmed by the user.
   * @return {void} Does not return any value.
   */
  confirmBookmarkCategoryDeletion(
    event: Event | undefined,
    bookmarkCategory: BookmarkCategory,
    onAccept: () => void
  ): void {
    this.confirmationService.confirm({
      key: 'bookmarkCategoryDeleteDialog',
      accept: () => {
        onAccept();
        this.messageService.add({
          severity: 'info',
          summary: 'Bestätigt',
          detail: `${bookmarkCategory.title} wurde gelöscht. Ausschließlich zugeordnete Ansichten und Berichte wurden mitgelöscht, mehrfach zugeordnete Ansichten bleiben in ihren übrigen Bereichen erhalten.`,
          life: 3000,
        });
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Abgebrochen',
          detail: `${bookmarkCategory.title} wurde nicht gelöscht`,
          life: 3000,
        });
      },
    });
  }

  /**
   * Displays a confirmation dialog to confirm the disable of the guest link for a given bookmark.
   * Based on the user's decision, it either deletes the guest link or cancels the operation.
   *
   * @param {Event | undefined} event - The event that triggered the confirmation. Can be undefined if not triggered by a user interaction event.
   * @param {Bookmark} bookmark - The bookmark object representing the item for which the guest link deletion is being confirmed.
   * @param {function} onAccept - A callback function to execute when the user confirms the deletion of the guest link.
   * @return {void} This method does not return a value.
   */
  confirmBookmarkGuestLinkDeletion(
    event: Event | undefined,
    bookmark: Bookmark,
    onAccept: () => void
  ): void {
    this.confirmationService.confirm({
      key: 'bookmarkGuestLinkDeleteDialog',
      accept: () => {
        onAccept();
        this.messageService.add({
          severity: 'info',
          summary: 'Bestätigt',
          detail: `Der Gast-Link für „${bookmark.title}“ wurde gelöscht und ist nicht mehr aufrufbar.`,
          life: 3000,
        });
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Abgebrochen',
          detail: `Der Gast-Link für „${bookmark.title}“ wurde nicht gelöscht und ist weiterhin aufrufbar`,
          life: 3000,
        });
      },
    });
  }

  /**
   * Confirms the disabling of a guest link for a bookmark.
   * Triggers a confirmation dialog and executes the provided callback on acceptance.
   * Displays appropriate feedback messages based on the user's decision.
   *
   * @param {Event | undefined} event - The event object, if applicable, triggering the confirmation process.
   * @param {Bookmark} bookmark - The bookmark object whose guest link is to be disabled.
   * @param {Function} onAccept - The callback function to execute when the confirmation is accepted.
   * @return {void} This method does not return a value.
   */
  confirmBookmarkGuestLinkToggle(
    event: Event | undefined,
    bookmark: Bookmark,
    onAccept: () => void
  ): void {
    this.confirmationService.confirm({
      key: 'bookmarkGuestLinkDisableDialog',
      accept: () => {
        onAccept();
        this.messageService.add({
          severity: 'info',
          summary: 'Bestätigt',
          detail: `Der Gast-Link für „${bookmark.title}“ wurde ${bookmark.guestLink?.enabled ? 'deaktiviert' : 'aktiviert'}.`,
          life: 3000,
        });
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Abgebrochen',
          detail: `Der Gast-Link für „${bookmark.title}“ wurde nicht ${bookmark.guestLink?.enabled ? 'deaktiviert' : 'aktiviert'}.`,
          life: 3000,
        });
      },
    });
  }

  /**
   * Displays a confirmation dialog to confirm the action of copying from a pool.
   *
   * @param onAccept A callback function that is executed upon confirming the action.
   * @param header An optional string specifying the header text for the confirmation dialog.
   * @param key An optional string defining the unique key for the confirmation dialog (default is 'copyFromPool').
   * @return void
   */
  confirmCopyFromPool(
    onAccept: () => void,
    header?: string,
    key = 'copyFromPool'
  ): void {
    this.confirmationService.confirm({
      header: header,
      key: key,
      accept: () => {
        onAccept();
        this.messageService.add({
          severity: 'info',
          summary: 'Bestätigt',
          detail: 'Die Indikatoren wurden erfolgreich kopiert.',
          life: 3000,
        });
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Abgebrochen',
          detail: 'Kopiervorgang abgebrochen',
          life: 3000,
        });
      },
    });
  }

  /**
   * Displays a confirmation dialog before starting a geo-data import that
   * reacts to a Gebietsstandsänderung: importing here creates a new, independent
   * GeoData record for just this one year instead of overwriting the record
   * shared with sibling years. Explains that other years keep their existing
   * boundaries, while years created after this one will inherit the new data.
   *
   * @param {number | string} year - The year (DataLayer) the import applies to.
   * @param {Function} onAccept - Callback executed once the user confirms.
   * @return {void} Does not return a value.
   */
  confirmGeoDataDecouple(year: number | string, onAccept: () => void): void {
    this.confirmationService.confirm({
      key: 'geoDataDecoupleDialog',
      message: `
        <p>
          Für <b>${year}</b> wird ein neues, eigenständiges Geodaten-Dokument
          angelegt. Andere Jahre bleiben unverändert; neu angelegte Jahre
          übernehmen künftig automatisch die neuen Geodaten.
        </p>
      `,
      icon: 'fas fa-map-location-dot',
      header: 'Gebietsstandsänderung importieren',
      acceptIcon: 'p-button-icon p-button-icon-left fas fa-cloud-upload',
      rejectIcon: 'p-button-icon p-button-icon-left fas fa-times',
      acceptLabel: 'ja, fortfahren',
      rejectLabel: 'nein, abbrechen',
      acceptButtonStyleClass: 'p-button-warning p-button-rounded',
      rejectButtonStyleClass: 'p-button-text p-button-rounded',
      accept: () => {
        onAccept();
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Abgebrochen',
          detail: 'Es wurden keine neuen Geodaten importiert.',
          life: 3000,
        });
      },
    });
  }

  /**
   * Displays a confirmation dialog for overwriting a bookmark. Executes the provided callback when the user accepts.
   *
   * @param {Function} onAccept - A callback function that is executed when the user confirms the overwrite action.
   * @param {string} [header] - An optional header text for the confirmation dialog.
   * @param {string} [key='overwriteBookmark'] - An optional key to identify the confirmation dialog instance.
   * @return {void} This method does not return a value.
   */
  confirmBookmarkOverwrite(
    onAccept: () => void,
    header?: string,
    key = 'overwriteBookmark'
  ): void {
    this.confirmationService.confirm({
      header: header,
      key: key,
      accept: () => {
        onAccept();
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Abgebrochen',
          detail: 'Die Ansicht wurde nicht gespeichert.',
          life: 3000,
        });
      },
    });
  }

  /**
   * Confirms atlas overwrite and returns a promise.
   *
   * @param {string} [header] - An optional header text for the confirmation dialog.
   * @param {string} [message] - An optional message text for the confirmation dialog.
   * @param {string} [key='newAtlasView'] - An optional key to identify the confirmation dialog instance.
   * @return {Promise<boolean>} A promise that resolves to true if accepted, false if rejected.
   */
  confirmAtlasOverwritePromise(
    header?: string,
    message?: string,
    key = 'newAtlasView'
  ): Promise<boolean> {
    return new Promise(resolve => {
      this.confirmationService.confirm({
        header: header,
        message: message,
        key: key,
        accept: () => {
          resolve(true);
        },
        reject: () => {
          resolve(false);
        },
      });
    });
  }

  /**
   * Asks whether a view should be published although the validation found
   * problems the public would see (empty widgets, broken references).
   *
   * @param {string} title - Title of the view (or a description of several).
   * @param {string[]} problems - Messages of the problems found.
   * @param {string} [header] - Replaces the default header.
   * @return {Promise<boolean>} true if the user wants to publish anyway.
   */
  confirmPublishWithIssues(
    title: string,
    problems: string[],
    header?: string
  ): Promise<boolean> {
    return new Promise(resolve => {
      this.confirmationService.confirm({
        header:
          header ||
          `„${title}“ hat ${problems.length} Problem${problems.length === 1 ? '' : 'e'}`,
        message: `
          <p>Öffentlich blieben dadurch Widgets leer oder fehlerhaft:</p>
          ${this.htmlList(problems)}
          <p><strong>Trotzdem veröffentlichen?</strong></p>
        `,
        icon: 'fas fa-triangle-exclamation',
        acceptLabel: 'Trotzdem veröffentlichen',
        rejectLabel: 'Abbrechen',
        accept: () => resolve(true),
        reject: () => resolve(false),
      });
    });
  }

  /**
   * Like confirmPublishWithIssues, with a third option to look at the
   * view's details ("Nutzung & Freigabe") instead of deciding right away.
   * Uses the dialog with key "publishIssues" in app.component.html.
   *
   * @param {string} title - Title of the view.
   * @param {string[]} problems - Messages of the problems found.
   * @return {Promise<'publish' | 'details' | 'cancel'>} The user's choice.
   */
  confirmPublishWithIssuesOrDetails(
    title: string,
    problems: string[]
  ): Promise<'publish' | 'details' | 'cancel'> {
    return new Promise(resolve => {
      this.confirmationService.confirm({
        key: 'publishIssues',
        header: `„${title}“ hat ${problems.length} Problem${problems.length === 1 ? '' : 'e'}`,
        message: `
          <p>Öffentlich blieben dadurch Widgets leer oder fehlerhaft:</p>
          ${this.htmlList(problems)}
          <p><strong>Trotzdem veröffentlichen?</strong></p>
        `,
        icon: 'fas fa-triangle-exclamation',
        accept: () => resolve('publish'),
        // the dialog's "Details ansehen" closes it (CANCEL), "Abbrechen" rejects
        reject: (type: ConfirmEventType) =>
          resolve(type === ConfirmEventType.CANCEL ? 'details' : 'cancel'),
      });
    });
  }

  /**
   * Asks whether the unpublished topics a view is filed under should be
   * published along with it - otherwise the public atlas doesn't list the
   * view there. Uses the dialog with key "publishTopics" in app.component.html.
   *
   * @param {string} title - Title of the view.
   * @param {string[]} topicNames - Names of the unpublished topics.
   * @return {Promise<'withTopics' | 'viewOnly' | 'cancel'>} The user's choice.
   */
  confirmPublishTopics(
    title: string,
    topicNames: string[]
  ): Promise<'withTopics' | 'viewOnly' | 'cancel'> {
    const several = topicNames.length > 1;
    return new Promise(resolve => {
      this.confirmationService.confirm({
        key: 'publishTopics',
        header: several
          ? 'Themen sind nicht veröffentlicht'
          : 'Thema ist nicht veröffentlicht',
        message: `
          <p>„${title}“ liegt in ${several ? 'folgenden Themen, die' : 'folgendem Thema, das'} nicht veröffentlicht ${several ? 'sind' : 'ist'}:</p>
          ${this.htmlList(topicNames)}
          <p>Im öffentlichen Atlas ist die Ansicht dort nicht zu finden.
          <strong>${several ? 'Themen' : 'Thema'} ebenfalls veröffentlichen?</strong></p>
        `,
        icon: 'fas fa-earth-europe',
        accept: () => resolve('withTopics'),
        // "Nur Ansicht veröffentlichen" closes the dialog (CANCEL), "Abbrechen" rejects
        reject: (type: ConfirmEventType) =>
          resolve(type === ConfirmEventType.CANCEL ? 'viewOnly' : 'cancel'),
      });
    });
  }

  /**
   * Asks whether everything a view still needs for its publication should
   * be published now ("Fehlendes freigeben").
   *
   * @param {string} title - Title of the view.
   * @param {string[]} items - Summary of what gets published, e.g. "2 Jahre".
   * @return {Promise<boolean>} true if the user confirmed.
   */
  confirmPublishRequirements(title: string, items: string[]): Promise<boolean> {
    return new Promise(resolve => {
      this.confirmationService.confirm({
        header: `Fehlendes für „${title}“ freigeben`,
        message: `
          <p>Folgendes wird veröffentlicht und ist danach im öffentlichen Atlas sichtbar:</p>
          ${this.htmlList(items)}
          <p class="text-sm text-500">Die Ansicht selbst bleibt unverändert.</p>
        `,
        icon: 'fas fa-earth-europe',
        acceptLabel: 'Jetzt freigeben',
        rejectLabel: 'Abbrechen',
        accept: () => resolve(true),
        reject: () => resolve(false),
      });
    });
  }

  /**
   * Asks whether indicator data should be switched to internal although
   * public views show it (their widgets would lose these values).
   *
   * @param {number} count - Number of indicator data records.
   * @param {string[]} viewTitles - Titles of the affected public views.
   * @return {Promise<boolean>} true if the user wants to go ahead.
   */
  confirmUnpublishUsedData(
    count: number,
    viewTitles: string[]
  ): Promise<boolean> {
    return new Promise(resolve => {
      this.confirmationService.confirm({
        header: `${viewTitles.length} öffentliche Ansicht${viewTitles.length === 1 ? ' nutzt' : 'en nutzen'} diese Daten`,
        message: `
          <p>Werden die ${count} Datensätze intern geschaltet, fehlen sie in:</p>
          ${this.htmlList(viewTitles)}
          <p><strong>Trotzdem intern schalten?</strong></p>
        `,
        icon: 'fas fa-triangle-exclamation',
        acceptLabel: 'Trotzdem intern schalten',
        rejectLabel: 'Abbrechen',
        accept: () => resolve(true),
        reject: () => resolve(false),
      });
    });
  }

  /**
   * Builds an HTML list (max. 5 entries) for a confirm dialog message. The
   * entries contain user data (view/indicator names) and the dialog renders
   * HTML, so they are escaped.
   */
  private htmlList(entries: string[], max = 5): string {
    const escape = (text: string) =>
      text.replace(
        /[&<>"']/g,
        char =>
          ({
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;',
          })[char] as string
      );
    const items = entries
      .slice(0, max)
      .map(entry => `<li>${escape(entry)}</li>`);
    if (entries.length > max) {
      items.push(`<li>… und ${entries.length - max} weitere</li>`);
    }
    return `<ul class="pl-3 text-sm">${items.join('')}</ul>`;
  }

  /**
   * Confirms removal of missing data layer references from bookmark widgets
   * Shows warning that the widgets will be modified
   *
   * @param {Event | undefined} event - The triggering event
   * @param {string} dataLayerName - Name of the missing data layer
   * @param {number} widgetCount - Number of widgets affected
   * @param {Function} onAccept - Callback executed on confirmation
   * @return {void}
   */
  confirmRemoveMissingDataLayer(
    event: Event | undefined,
    dataLayerName: string,
    widgetCount: number,
    onAccept: () => void
  ): void {
    this.confirmationService.confirm({
      target: event?.target as EventTarget,
      message: `
        <p>
          Das Jahr <b>"${dataLayerName}"</b> wurde nicht gefunden und wird in
          <b>${widgetCount} Darstellung${widgetCount > 1 ? 'en' : ''}</b> referenziert.
        </p>
        <p>
          <strong>Die Referenz kann sicher aus der Darstellung entfernt werden!</strong> <br>
          Das Jahr wurde gelöscht. Überprüfen Sie die Darstellung in Ihrer Ansicht, um die Richtigkeit zu überprüfen.
        </p>
        <p class="text-sm text-500">
          Die Darstellungen werden aktualisiert und die Ansicht wird gespeichert.
        </p>
      `,
      icon: 'fas fa-info-circle',
      header: 'Fehlende Jahr-Referenz entfernen',
      acceptIcon: 'p-button-icon p-button-icon-left fas fa-eraser',
      rejectIcon: 'p-button-icon p-button-icon-left fas fa-times',
      acceptLabel: 'Ja, entfernen',
      rejectLabel: 'Nein, abbrechen',
      acceptButtonStyleClass: 'p-button-warning p-button-rounded',
      rejectButtonStyleClass: 'p-button-text p-button-rounded',
      accept: () => {
        onAccept();
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Abgebrochen',
          detail: 'Die Referenz wurde nicht entfernt',
          life: 3000,
        });
      },
    });
  }

  /**
   * Confirms removal of missing indicator references from bookmark widgets
   * Shows warning that the widgets will be modified
   *
   * @param {Event | undefined} event - The triggering event
   * @param {string} indicatorName - Name of the missing indicator
   * @param {number} widgetCount - Number of widgets affected
   * @param {Function} onAccept - Callback executed on confirmation
   * @return {void}
   */
  confirmRemoveMissingIndicator(
    event: Event | undefined,
    indicatorName: string,
    widgetCount: number,
    onAccept: () => void
  ): void {
    this.confirmationService.confirm({
      target: event?.target as EventTarget,
      message: `
        <p>
          Der Indikator mit der ID <b>"${indicatorName}"</b> wurde nicht gefunden und wird in
          <b>${widgetCount} Darstellung${widgetCount > 1 ? 'en' : ''}</b> referenziert.
        </p>
        <p>
          <strong>Die Referenz kann sicher aus der Darstellung entfernt werden!</strong> <br>
          Der Indikator wurde gelöscht. Überprüfen Sie die Darstellung in Ihrer Ansicht, um die Richtigkeit zu überprüfen.
        </p>
        <p class="text-sm text-500">
          Die Darstellungen werden aktualisiert und die Ansicht wird gespeichert.
        </p>
      `,
      icon: 'fas fa-info-circle',
      header: 'Fehlende Indikator-Referenz entfernen',
      acceptIcon: 'p-button-icon p-button-icon-left fas fa-eraser',
      rejectIcon: 'p-button-icon p-button-icon-left fas fa-times',
      acceptLabel: 'Ja, entfernen',
      rejectLabel: 'Nein, abbrechen',
      acceptButtonStyleClass: 'p-button-warning p-button-rounded',
      rejectButtonStyleClass: 'p-button-text p-button-rounded',
      accept: () => {
        onAccept();
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Abgebrochen',
          detail: 'Die Referenz wurde nicht entfernt',
          life: 3000,
        });
      },
    });
  }

  /**
   * Confirms publication of ALL issues in a bookmark (BATCH FIX)
   * Shows STRONG warning due to mass publication risk
   *
   * @param {Event | undefined} event - The triggering event
   * @param {string} bookmarkTitle - Title of the bookmark
   * @param {number} issueCount - Number of issues to be fixed
   * @param {Function} onAccept - Callback executed on confirmation
   * @return {void}
   */
  confirmValidationFixAll(
    event: Event | undefined,
    bookmarkTitle: string,
    issueCount: number,
    onAccept: () => void
  ): void {
    this.confirmationService.confirm({
      target: event?.target as EventTarget,
      message: `
        <div>
          <h3 class="flex gap-3 text-red-600">
            <u>ACHTUNG!</u> Sie veröffentlichen eventuell unkontrolliert Daten!
          </h3>
        </div>
        <p>
          Sie sind dabei, <b>${issueCount} Problem(e)</b> in der Ansicht
          <i>"${bookmarkTitle}"</i> automatisch zu beheben.
        </p>
        <p>
          <strong>Das bedeutet:</strong>
        </p>
        <ul>
          <li><b>Mehrere</b> Jahre und/oder Datensätze werden veröffentlicht!</li>
          <li>Diese Daten werden <b>sofort</b> öffentlich sichtbar!</li>
          <li>Dies kann <b>nicht</b> rückgängig gemacht werden!</li>
        </ul>
        <p class="font-bold">
          Sind Sie <u>sicher</u>, dass Sie fortfahren möchten?
        </p>
        <p class="font-italic">
          Alternativ können Sie die Probleme manuell beheben.
        </p>
      `,
      icon: 'fas fa-exclamation-triangle',
      header: `${issueCount} Problem(e) automatisch beheben`,
      acceptIcon: 'p-button-icon p-button-icon-left fas fa-wand-magic-sparkles',
      rejectIcon: 'p-button-icon p-button-icon-left fas fa-times',
      acceptLabel: 'Ja, ALLE veröffentlichen',
      rejectLabel: 'Nein, abbrechen',
      acceptButtonStyleClass: 'p-button-danger p-button-rounded',
      rejectButtonStyleClass: 'p-button-text p-button-rounded',
      accept: () => {
        onAccept();
        this.messageService.add({
          severity: 'success',
          summary: 'Veröffentlichung abgeschlossen',
          detail: `Alle Probleme in "${bookmarkTitle}" wurden behoben.`,
          life: 5000,
        });
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Abgebrochen',
          detail: 'Es wurden keine Daten veröffentlicht',
          life: 3000,
        });
      },
    });
  }

  /**
   * Retrieves the title associated with a specific entity based on its type.
   *
   * @param {any} item - The entity object containing properties used to determine its title.
   * @param {string} entityType - The type of the entity, which determines how the title is extracted.
   * @return {string} - Returns the title of the entity, or a default value if no title is available.
   */
  getTitleByEntity(item: any, entityType: string): string {
    switch (entityType) {
      case 'User':
        return item.username || 'Nutzer:in';
      case 'DataLayer':
        return item.year || 'Jahr';
      case 'IndicatorData':
        return 'Datensatz';
      case 'BookmarkCategory':
        return item.title || 'Themenbereich';
      case 'Bookmark':
        return item.title || 'Ansicht';
      case 'Story':
        return item.title || 'Bericht';
      default:
        return item.name || item.title || 'Datensatz';
    }
  }

  /**
   * Retrieves the title translation corresponding to a given entity type.
   *
   * @param {string} entityType - The type of entity for which the title is to be retrieved.
   *                              Expected values are 'User', 'DataLayer', 'IndicatorData',
   *                              'BookmarkCategory', 'Bookmark', or 'Story'.
   * @return {string} The title associated with the provided entity type. Defaults to 'Datensätze'
   *                  if the entity type does not match any predefined values.
   */
  getTitleByEntityMultiple(entityType: string): string {
    switch (entityType) {
      case 'User':
        return 'Nutzer:innen';
      case 'DataLayer':
        return 'Jahre';
      case 'Indicator':
        return 'Indikatoren';
      case 'IndicatorData':
        return 'Daten';
      case 'BookmarkCategory':
        return 'Themenbereiche';
      case 'Bookmark':
        return 'Ansichten';
      case 'Story':
        return 'Berichte';
      default:
        return 'Datensätze';
    }
  }
}
